
import { Component, Prop, Mixins, Provide } from "vue-property-decorator";
import { CreateElement } from "vue";

import RequestPromises from "@/mixins/requestPromises";
import { IProvider } from "@/models/providers";
import { ITemplate, ITemplateContent } from "@/models/templates";
import {
  ITemplateProvider,
  ITemplateProviderMethods
} from "@/models/providers/templates";
import { TemplateTypes } from "@/data/enums/templates";
import TemplatesCategoriesMixin from "@/mixins/templatesCategoriesMixin";
import TemplatesChannelsMixin from "@/mixins/templatesChannelsMixin";
import TemplateMixin from "@/mixins/templateMixin";
import { NotificationChannelCodes } from "@/data/enums/notifications";
import { UpmindObjectToDataModuleMapping } from "@/data/enums/objects";
import { DataModules } from "@/store/modules/data";
import { ConfirmModalModes } from "@/data/enums";

@Component
export default class TemplateProvider extends Mixins(RequestPromises)
  implements IProvider {
  // Props
  @Prop({ default: null })
  public readonly templateId!: ITemplate["id"];

  @Prop({ required: true })
  public readonly templateType!: TemplateTypes;

  @Prop({ default: null })
  public readonly templateCode!: ITemplate["code"];

  @Prop({ default: false })
  public readonly isReadOnly!: boolean;

  @Prop({ default: true })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  @Prop({ default: () => () => ({}) })
  public onNotFound!: IProvider["onNotFound"];

  // Data

  public isLoadingCategories = true;
  public isLoading = true;
  public isReloading = false;
  public isProcessing = false;
  public hasRequestError = false;

  public templateMixin = new TemplateMixin({
    parent: this.$parent,
    propsData: {
      templateId: this.templateId,
      templateCode: this.templateCode,
      templateType: this.templateType
    }
  });

  public categoriesMixin = new TemplatesCategoriesMixin({
    parent: this.$parent,
    propsData: {
      scope: `${this.templateId}_categories`
    }
  });
  public channelsMixin = new TemplatesChannelsMixin({
    parent: this.$parent,
    propsData: {
      scope: `${this.templateId}_categories`
    }
  });

  // Life-cycle Methods

  public created() {
    this.loadData();
  }

  public beforeDestroy() {
    this.categoriesMixin.$destroy();
    this.channelsMixin.$destroy();
    this.templateMixin.$destroy();
  }

  // Provide

  @Provide("templateProvider")
  public providerData(): ITemplateProvider {
    return {
      brandCategory: this.categoriesMixin.brandCategory,
      canCreate: this.templateMixin.canCreate,
      canDelete: this.templateMixin.canDelete,
      canDuplicate: this.templateMixin.canDuplicate,
      canEdit: this.templateMixin.canEdit,
      canSave: this.templateMixin.canSave,
      canSendTestEmail: this.canSendTestEmail,
      categories: this.categoriesMixin.categories,
      channels: this.channelsMixin.channels,
      hasRequestError: this.hasRequestError,
      hooks: this.categoriesMixin.allHooks,
      isClientAreaTemplate: this.isClientAreaTemplate,
      isDefault: this.templateMixin.isDefault,
      isLoading: this.isLoading,
      isLoadingCategories: this.isLoadingCategories,
      isNew: this.templateMixin.isNew,
      isNotificationTemplate: this.isNotificationTemplate,
      isProcessing: this.isProcessing,
      isReadOnly: this.isReadOnly,
      isReloading: this.isReloading,
      recipientTypes: this.channelsMixin.allRecipientTypes,
      scope: this.templateMixin.templateScope,
      template: this.templateMixin.template,
      templateId: this.templateMixin.templateId,
      templateType: this.templateType
    };
  }

  @Provide("templateProviderMethods")
  public get providerMethods(): ITemplateProviderMethods {
    return {
      createTemplate: this.templateMixin.createTemplate,
      deleteTemplate: this.templateMixin.deleteTemplate,
      createContents: this.createContents,
      updateContents: this.updateContents,
      duplicate: this.duplicate,
      getTemplate: this.templateMixin.getTemplate,
      reloadContents: this.templateMixin.reloadContents,
      reloadData: this.reloadData,
      setIsProcessing: this.setIsProcessing,
      updateTemplate: this.templateMixin.updateTemplate,
      openAddEditModal: this.openAddEditModal,
      openViewContentModal: this.openViewContentModal,
      restoreToDefault: this.restoreToDefault
    };
  }

  // Computed

  public get isNotificationTemplate() {
    return this.templateType === TemplateTypes.NOTIFICATION;
  }

  public get isClientAreaTemplate() {
    return this.templateType === TemplateTypes.CLIENT_TEMPLATE;
  }

  public get isNew() {
    return this.templateMixin.isNew;
  }

  public get templateCategoryCode() {
    return this.templateMixin?.template?.category?.code;
  }

  public get templateChannelCode() {
    return this.templateMixin?.template?.channel?.code;
  }

  public get canSendTestEmail() {
    return !!(
      !this.isNew &&
      this.templateCategoryCode &&
      this.isNotificationTemplate &&
      this.templateChannelCode === NotificationChannelCodes.EMAIL &&
      this.$_.has(
        UpmindObjectToDataModuleMapping,
        this.templateCategoryCode || ""
      )
    );
  }

  // Methods

  public async loadData({ ignoreStored } = { ignoreStored: false }) {
    try {
      await Promise.all([
        this.templateMixin.getTemplate({ ignoreStored }),
        this.channelsMixin.getAll(),
        this.categoriesMixin
          .getAll()
          .then(() => (this.isLoadingCategories = false))
      ]);
    } catch (error) {
      const is403 = this.$store.getters["api/is403"](error);
      const is404 = this.$store.getters["api/is404"](error);
      if ((is403 || is404) && this.onNotFound) this.onNotFound(this);
      this.hasRequestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    return this.loadData({ ignoreStored: true });
  }

  public setIsProcessing(isProcessing) {
    this.isProcessing = isProcessing;
  }

  public async createContents(data: Partial<ITemplateContent>) {
    let templateId = this.templateMixin.template.id;
    if (this.templateMixin.isDefault) {
      const copy = await this.duplicate(this.templateId);
      templateId = copy.id;
    }

    const contents = (await this.templateMixin.createContents(
      templateId,
      data
    )) as ITemplateContent;

    if (this.templateMixin.isDefault) {
      this.$emit("changed", { templateId: contents.template_id });
    }

    return contents;
  }

  public async updateContents(
    templateContentId: ITemplateContent["id"],
    data: Partial<ITemplateContent>
  ) {
    let templateId = this.templateMixin.template.id;
    if (this.templateMixin.isDefault) {
      const copy = await this.duplicate(this.templateId);
      templateId = copy.id;
    }
    const contents = (await this.templateMixin.updateContents(
      templateContentId,
      templateId,
      data
    )) as ITemplateContent;

    if (this.templateMixin.isDefault) {
      this.$emit("changed", {
        templateId: contents.template_id,
        oldTemplateId: this.templateId
      });
    }

    return contents;
  }

  public async duplicate(id: ITemplate["id"]) {
    return this.templateMixin.duplicate(id);
  }

  public async restoreToDefault() {
    const confirm = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: false,
          confirmButtonText: this.$t("_action.restore_template"),
          message: this.$t("_sentence.templates.confirm_restore_to_default"),
          mode: ConfirmModalModes.BUTTON
        },
        events: {
          confirmed: async () => {
            await this.deleteTemplate(this.templateMixin.template.id);
            confirm.close();
            this.$emit("defaultRestored", {
              templateId: this.templateMixin.template.original_template_id
            });
          }
        }
      }
    });
  }

  public async deleteTemplate(templateId: ITemplate["id"]): Promise<any> {
    try {
      await this.$store.dispatch(`data/${DataModules.TEMPLATES}/remove`, {
        id: templateId
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  public async openAddEditModal(
    payload: {
      templateId?: ITemplate["id"];
      props?: any;
      events?: any;
    } = { templateId: undefined, props: {}, events: {} }
  ) {
    return this.$store.dispatch(
      `data/${DataModules.TEMPLATES}/openAddEditModal`,
      {
        props: {
          templateId: payload.templateId,
          templateType: this.templateType,
          showCode: false,
          ...payload.props
        }
      }
    );
  }

  public async openViewContentModal(props: any) {
    return this.$store.dispatch(
      `data/${DataModules.TEMPLATES}/openViewContentModal`,
      {
        props: {
          ...props,
          templateType: this.templateType
        }
      }
    );
  }

  // Render

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });
    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isLoading && this.hasRequestError },
        on: { click: () => this.reloadData() }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $templateData: this.providerData(),
        $templateMethods: this.providerMethods
      })
    );
  }
}
